import { css, styled } from 'styled-components';

import { VersionedRouterLink } from '#/components/VersionedLink/VersionedRouterLink';
import { scrolledDownHiddenVar, scrolledDownZeroVar } from '#/theme/scrolled';
import { colors, cssBreakpoints, gutterWidth, transition } from '#/theme/theme';

const layoutHeaderMobileLinksHeight = 64;

export const LayoutHeaderHolder = styled.div<{ $isInSearchPage: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;

  display: flex;
  flex-direction: column;
  background: ${colors.white};
  box-shadow: 0 2px 4px rgb(0 0 0 / 7.5%);
  font-size: 15px;
  line-height: normal;

  @media print {
    display: none;
  }
`;

export const LayoutHeaderTopSectionHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${gutterWidth / 2}px;
  height: 58px;
  position: relative;
`;

export const LayoutHeaderBottomSectionHolder = styled.div<{
  $isInSearchPage: boolean;
  $withScrolledTransition: boolean;
}>`
  display: flex;
  flex-direction: column;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    ${(props) =>
      props.$isInSearchPage
        ? css`
            transition: ${
              props.$withScrolledTransition && `padding-top ${transition}`
            };
            padding-top: calc(
              ${layoutHeaderMobileLinksHeight}px *
                var(${scrolledDownZeroVar}, 1)
            );
          `
        : css`
            padding-top: ${layoutHeaderMobileLinksHeight}px;
          `};
  }
`;

export const LayoutHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

export const LayoutHeaderLinksHolder = styled.div<{ $isInSearchPage: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${cssBreakpoints.mdDown}) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: ${layoutHeaderMobileLinksHeight}px;
    border-top: 1px solid ${colors.selectionGrey};
    justify-content: space-evenly;
    gap: 0;
  }

  ${(props) =>
    props.$isInSearchPage &&
    css`
      @media all and (max-width: ${cssBreakpoints.mdDown}) {
        transition: height ${transition};
        height: calc(
          ${layoutHeaderMobileLinksHeight}px * var(${scrolledDownZeroVar}, 1)
        );
        overflow: var(${scrolledDownHiddenVar});
      }
    `};
`;

// Only display the API & Integrations button on desktop up to a certain point
// This is a magical number - it was just a rough estimate of what fits in the current UI
const apiIntegrationsDropdownCutoffPoint = '1140px';

export const LayoutHeaderApiAndIntegrationsDropdownHolder = styled.div`
  @media (max-width: ${apiIntegrationsDropdownCutoffPoint}) {
    display: none;
  }
`;

export const LayoutHeaderDesktopDonateLinkHolder = styled.div`
  @media (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;

export const LayoutHeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const LayoutHeaderLogoLink = styled(VersionedRouterLink)`
  display: inline-flex;
  margin-right: 24px;
`;

export const LayoutHeaderCartDropdownButtonHolder = styled.div`
  display: flex;
  align-items: center;
  margin-left: 6px;
  z-index: 100;
`;

export const LayoutHeaderNotificationsDropdownHolder = styled.div`
  @media (min-width: ${cssBreakpoints.mdUp}) {
    margin-left: 6px;
  }
`;

export const LayoutHeaderAuthLink = styled.a`
  padding: 0 4px;
  color: ${colors.mediumContentGrey};
  font-size: inherit;
  transition: color ${transition};

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

const layoutHeaderAuthHolderDesktopGap = 16;
export const LayoutHeaderAuthHolder = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${layoutHeaderAuthHolderDesktopGap}px;

  @media (max-width: ${cssBreakpoints.mdUp}) {
    margin-right: ${layoutHeaderAuthHolderDesktopGap / 2}px;
  }

  a {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      right: -${layoutHeaderAuthHolderDesktopGap / 2}px;
      transform: translateY(-50%);
      width: 1px;
      height: 20px;
      background: ${colors.elementGrey};
      opacity: 0.5;
    }

    @media (min-width: ${cssBreakpoints.mdUp}) {
      &:last-child {
        &::before {
          display: none;
        }
      }
    }
  }
`;

export const LayoutHeaderSignupLinkHolder = styled.div`
  @media all and (max-width: 320px) {
    display: none;
  }
`;

export const LayoutHeaderMobileSearchHolder = styled.div`
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: none;
  }
`;

export const LayoutHeaderMyAccountHolder = styled.div`
  &::before {
    display: none;
  }
`;

export const LayoutHeaderDesktopSearchHolder = styled.div`
  padding: 16px ${gutterWidth / 2}px;
  background-color: ${colors.white};
  border-top: 1px solid ${colors.selectionGrey};

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;
export const LayoutHeaderProgressBarHolder = styled.div`
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: 1;
`;
